import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon } from 'lucide-react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import IconizedDialog from '@/components/atoms/IconizedDialog/IconizedDialog';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import useBrains from '@/hooks/useBrains';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogNodesCache from '@/hooks/useDialogNodesCache';
import useDialogs from '@/hooks/useDialogs';
import useIntents from '@/hooks/useIntents';
import { useIsStartingNode } from '@/hooks/useIsStartingNode';
import { cn } from '@/lib/utils';
import { languageSupportsEnhance } from '@/redux/dialogs/helper';
import { selectSelectedNode } from '@/redux/dialogs/selectors';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { updateNode } from '@/redux/nodes/actions';
import { selectAccountId, selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { OptionBase } from '@/shared/types/common';
import { RootState } from '@/shared/types/state';
import { INTENT_TRIGGER_DOCS_URL } from '@/util/constants';
import { formatErrorMessage } from '@/util/util';
import { intentFormSchema } from '@/util/validation/schemas/dialog-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';
import { AssignedReminders } from '../AssignedReminders';
import { StartingDialogBanner } from '../StartingDialogBanner/StartingDialogBanner';
import { nodeType } from '../ToolkitNodeEvent/util';

type Option = OptionBase;

type FormType = { name: string; intent: Option; enhance: boolean };

// Clean the intent value from the # character
const clean = (input: Option) => input?.label?.replace('#', '');

function ToolkitNodeIntent() {
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);

  const { cleanIntents, listStatus } = useIntents(brainId);
  const dispatch = useDispatch();
  const { nodeId, name, intent, enhance } = useSelector((state: RootState) => {
    const selectedNode = selectSelectedNode(state);
    return {
      nodeId: selectedNode.node_id,
      name: selectedNode.name ?? '',
      enhance: selectedNode?.enhance ?? false,
      intent: selectedNode.intent ?? '',
    };
  }, shallowEqual);

  const { findUsedNodes } = useDialogNodesCache();
  const usedIn = findUsedNodes({ brainId, name: nodeId });
  const nodesLength = usedIn?.length;
  const { dialogs } = useDialogs(brainId);
  const accountId = useSelector(selectAccountId);
  const features = useSelector(selectFeatureFlags);

  const showAIEnhancement = languageSupportsEnhance(brain?.language, accountId, features);
  const isStartingNode = useIsStartingNode(nodeId);

  const defaultIntent = { value: intent, label: `#${intent}` };

  const formMethods = useForm<FormType>({
    mode: 'onChange',
    defaultValues: { name, intent: defaultIntent, enhance },
    resolver: yupResolver(intentFormSchema) as Resolver<FormType>,
  });
  const {
    formState: { errors },
    getValues,
    control,
    setValue,
    trigger,
  } = formMethods;

  // Create the options for the intents select
  const options = cleanIntents?.map((i) => ({ value: i.intent, label: `#${i.intent}` })) ?? [];

  const onChange = useCallback(() => {
    const values = getValues();

    dispatch(updateNode({ nodeId, node: { ...values, intent: clean(values.intent) } }));
  }, [getValues, dispatch, nodeId]);

  // Reset the form when the node id changes
  useEffect(() => {
    setValue('intent', { value: intent, label: `#${intent}` });
  }, [intent, setValue]);

  const handleIntentChange = useCallback(
    (value: string) => {
      setValue('intent', { value, label: `#${value}` });
      onChange();
    },
    [onChange, setValue]
  );

  const nameErrorMessage = errors.name?.message;
  const intentErrorMessage = useMemo(() => {
    if (!intent) {
      return t('dialog.intent_required');
    }
    if (cleanIntents?.some((i) => i.intent === intent)) {
      return undefined;
    }
    return t('dialog.intent_not_found');
  }, [intent, cleanIntents, t]);

  const { setFieldError } = useDialogAlert({
    id: nodeId,
    nodeId,
    title: t('dialog.intent'),
    type: 'intent',
  });

  useEffect(() => {
    setFieldError('name', nameErrorMessage);
  }, [nameErrorMessage, setFieldError]);

  useEffect(() => {
    if (listStatus !== 'success') {
      return;
    }
    setFieldError('intent', intentErrorMessage);
  }, [intentErrorMessage, listStatus, setFieldError]);

  useEffect(() => {
    // Trigger validation on mount
    trigger();
  }, [trigger]);

  return (
    <ToolkitWrapper type="intent">
      <Form {...formMethods}>
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel>{t('dialog.node_name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('dialog.webview.type_name')}
                  onChange={(event) => {
                    field.onChange(event);
                    onChange();
                  }}
                />
              </FormControl>
              <FormMessage>{formatErrorMessage(errors.name)}</FormMessage>
            </FormItem>
          )}
        />
      </Form>

      <div className="mb-6">
        <label className="mb-2 flex items-center gap-1 text-xs font-semibold text-foreground-muted uppercase">
          {t('dialog.intent')}
          <InfoTooltip
            content={t('dialog.select_intent_for_node')}
            learnMoreUrl={INTENT_TRIGGER_DOCS_URL}
            side="right"
          >
            <CircleHelpIcon size={12} className="text-foreground-muted" />
          </InfoTooltip>
        </label>
        <FormField
          control={control}
          name="intent.value"
          render={({ field }) => (
            <>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  handleIntentChange(value);
                }}
              >
                <SelectTrigger
                  className={cn(
                    'w-full',
                    (errors.intent || intentErrorMessage) && 'border-destructive'
                  )}
                >
                  <SelectValue placeholder={t('intent.select_an_intent')} />
                </SelectTrigger>
                <SelectContent>
                  {/* Hidden item so the Select trigger displays the non-existent intent name */}
                  {intent && intentErrorMessage && (
                    <SelectItem key={intent} value={intent} disabled className="hidden">
                      {`#${intent}`}
                    </SelectItem>
                  )}
                  {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {intentErrorMessage && (
                <p className="mt-1 text-sm text-destructive">{intentErrorMessage}</p>
              )}
            </>
          )}
        />
      </div>

      {showAIEnhancement && (
        <Controller
          name="enhance"
          control={control}
          render={({ field: { value, onChange: onControllerChange } }) => {
            return (
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm">{t('dialog.unknown.enhance_with_ai')}</span>
                <InfoTooltip content={t('dialog.unknown.enhance_with_ai_tooltip')} side="right">
                  <CircleHelpIcon size={12} className="text-foreground-muted" />
                </InfoTooltip>
                <Switch
                  checked={value}
                  onCheckedChange={(newValue) => {
                    onControllerChange(newValue);
                    onChange();
                  }}
                />
              </div>
            );
          }}
        />
      )}

      {
        <div className="my-6">
          <AssignedReminders />
        </div>
      }

      {nodesLength > 0 && (
        <div className="mt-8">
          <div className="mb-1">
            <span className="text-sm leading-5 font-medium text-foreground-muted">
              {t('dialog.event.used_by')}
            </span>
          </div>
          <div className="mb-3">
            <span className="text-sm leading-5 font-normal text-foreground-muted">
              <Trans i18nKey="dialog.event.jump_to" />
            </span>
          </div>
          <div className="flex max-h-80 flex-col items-start overflow-y-auto pt-1 [&_path]:fill-current [&_path]:stroke-current">
            {usedIn.map((x) => (
              <button
                key={`${x.nodeId}-${x.label}`}
                type="button"
                onClick={() =>
                  navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${x.dialogId}`)
                }
                className={cn('ml-1.5 flex items-center gap-1 bg-inherit pb-1 text-informational', {
                  '[&_path]:!fill-none': nodeType(x.dialogId, dialogs) === 'event',
                })}
              >
                <IconizedDialog
                  key={x.nodeId}
                  dialogName={x.label}
                  type={nodeType(x.dialogId, dialogs)}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {isStartingNode && <StartingDialogBanner />}
    </ToolkitWrapper>
  );
}

export default ToolkitNodeIntent;
